// /src/pages/private/admin/orgStructureTabs/Division.jsx

import React, { useState } from 'react';

const idEquals = (a, b) => String(a) === String(b);

const Division = ({ division, divisions, onNodeClick, isSelected, selectedDivisionId, expandedOrder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const children = divisions.filter(d => d.parentId === division.id);
  const hasChildren = children.length > 0;

  const parentDivision = divisions.find(d => idEquals(d.id, division.parentId));
  /** Nœud racine (ex. Executive) : pas de parent — rendu dédié, nom depuis divisionFullName. */
  const isRootExecutiveNode = division.parentId == null || division.parentId === '';
  /** Enfant direct de l'exécutif (parent sans parent) = niveau « Divisions ». */
  const isOrgDivisionTier = Boolean(parentDivision && (parentDivision.parentId == null || parentDivision.parentId === ''));
  /** Sous une division (parent a lui-même un parent) = niveau « Branches ». */
  const isOrgBranchTier = Boolean(
    parentDivision && parentDivision.parentId != null && parentDivision.parentId !== '',
  );
  /** Grosses tuiles : uniquement nœuds avec enfants (sections = sans enfant → taille logo par défaut). */
  const showOrgDivisionSizing = isOrgDivisionTier && hasChildren;
  const showOrgBranchSizing = isOrgBranchTier && hasChildren;

  const leafChildren = children.filter(child => !divisions.some(d => d.parentId === child.id));
  const nonLeafChildren = children.filter(child => divisions.some(d => d.parentId === child.id));

  const formatTextWithLineBreaks = text => (
    <>
      {text.split('\n').map((line, index) => (
        <span key={index}>{line}{index < text.split('\n').length - 1 && <br />}</span>
      ))}
    </>
  );

  const getLocalStyleSheet = () => {
    for (const sheet of document.styleSheets) {
      try {
        sheet.cssRules; // Test lecture
        return sheet;
      } catch (e) {
        // Sheet inaccessible, on skip
      }
    }
    return null;
  };

  const createDynamicCSS = (() => {
    const cache = new Set();
  
    return (className, styles) => {
      if (cache.has(className)) return className;
  
      const styleSheet = getLocalStyleSheet();
      if (!styleSheet) {
        console.warn("No accessible stylesheet found for dynamic CSS.");
        return className;
      }
  
      const styleStr = Object.entries(styles)
        .map(([k, v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${v}`)
        .join("; ");
  
      styleSheet.insertRule(`.${className} { ${styleStr} }`, styleSheet.cssRules.length);
      cache.add(className);
      return className;
    };
  })();
  

  /** Nœuds sans enfant direct : fond éclairci (~8 % blanc sur le dégradé API). */
  const getDivisionStyleClass = (div, { noDirectChildren = false } = {}) => {
    const { elementBorderColor, elementTextColor, elementColor1, elementColor2 } = div;
    const tone = noDirectChildren ? 'leaf08' : 'std';
    const safeName = `division-${tone}-${elementBorderColor?.slice(1)}-${elementTextColor?.slice(1)}-${elementColor1?.slice(1)}-${elementColor2?.slice(1)}`.replace(/[^a-zA-Z0-9-_]/g, '');
    const baseGradient = `linear-gradient(${elementColor1}, ${elementColor2})`;
    const background = noDirectChildren
      ? `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), ${baseGradient}`
      : baseGradient;
    return createDynamicCSS(safeName, {
      border: `1px solid ${elementBorderColor}`,
      color: elementTextColor,
      background
    });
  };

  const branchNodeSelected =
    selectedDivisionId !== undefined
      ? selectedDivisionId != null && idEquals(division.id, selectedDivisionId)
      : Boolean(isSelected);

  const leafNodeSelected = (leaf) =>
    selectedDivisionId !== undefined
      ? selectedDivisionId != null && idEquals(leaf.id, selectedDivisionId)
      : false;

  const LeafNodes = ({ leaves }) => (
    <div className="tree-column">
      {leaves.map(leaf => (
        <div key={leaf.id} 
        className={`tree-node ${leaf.divisionImageUrl ? 'with-image' : 'without-image'} ${leaf.divisionFullName === 'Access denied' ? 'redacted' : ''} ${leafNodeSelected(leaf) ? 'selected' : ''} ${getDivisionStyleClass(leaf, { noDirectChildren: true })}`}
             onClick={(event) => {
               //console.log("Leaf node clicked, ID:", leaf.id);
               event.stopPropagation();
               onNodeClick(leaf.id);
             }}
        >
          {leaf.divisionImageUrl && <img src={leaf.divisionImageUrl} alt="Division" className="division-image" />}
          <div className="division-content">
            <div className="division-name">{formatTextWithLineBreaks(leaf.divisionFullName)}</div>
            {leaf.elementDetails && <div className="division-details">{formatTextWithLineBreaks(leaf.elementDetails)}</div>}
          </div>
        </div>
      ))}
    </div>
  );

  const handleDivisionClick = (event) => {
    //console.log("Clic sur le noeud avec ID:", division.id);  // Log pour voir quel ID est cliqué
    event.stopPropagation();  // Stoppe la propagation pour isoler l'événement
    onNodeClick(division.id);  // Déclenche l'action avec l'ID du noeud cliqué
    setIsExpanded(!isExpanded); // Toggle the expanded state
  };

  const getClassName = () => {
    if (division.order === "1") {
      return "expanded";
    }
    if (division.order >= 2 && division.order <= 6) {
      return division.order === expandedOrder ? "expanded" : "reduced";
    }
    return "";
  };

  return (
    <div className={`tree-branch ${getClassName()}`} onClick={handleDivisionClick}>
      {isRootExecutiveNode ? (
        <div
          className={`tree-node tree-node--executive-root ${
            division.divisionFullName === 'Access denied' ? 'redacted' : ''
          } ${branchNodeSelected ? 'selected' : ''}`}
        >
          <div className="executive-root-banner">
            <div className="executive-root-stars" aria-hidden>
              <span className="executive-root-star">★</span>
              <span className="executive-root-star executive-root-star--mid">★</span>
              <span className="executive-root-star">★</span>
            </div>
            <div className="executive-root-title-row">
              <span className="executive-root-line" />
              <h2 className="executive-root-title">{formatTextWithLineBreaks(division.divisionFullName)}</h2>
              <span className="executive-root-line" />
            </div>
            <p className="executive-root-sub">
              Please choose a division from below to view its description.
            </p>
            <svg
              className="executive-root-frame-line"
              viewBox="0 0 1200 52"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                d="M0 8 L380 8 L450 44 L750 44 L820 8 L1200 8"
                fill="none"
                stroke="#c8aa6e"
                strokeWidth="1.5"
                vectorEffect="nonScalingStroke"
              />
            </svg>
          </div>
        </div>
      ) : (
      <div className={`tree-node ${division.divisionImageUrl ? 'with-image' : 'without-image'} ${showOrgDivisionSizing ? 'tree-node--org-division' : ''} ${showOrgBranchSizing ? 'tree-node--org-branch' : ''} ${division.divisionFullName === 'Access denied' ? 'redacted' : ''} ${branchNodeSelected ? 'selected' : ''} ${getDivisionStyleClass(division, { noDirectChildren: !hasChildren })}`}>
          <div className="image-container">
            {division.divisionImageUrl && <img src={division.divisionImageUrl} alt="Division" className="division-image" />}
          </div>
        
        <div className="division-content">
          <div className="division-name">{formatTextWithLineBreaks(division.divisionFullName)}</div>
          {division.elementDetails && <div className="division-details">{formatTextWithLineBreaks(division.elementDetails)}</div>}
        </div>
      </div>
      )}
      {hasChildren && (
        <div className="tree-children">
          {leafChildren.length > 0 && <LeafNodes leaves={leafChildren} />}
          {nonLeafChildren.length > 0 && nonLeafChildren.map(child => (
            <Division key={child.id} division={child} divisions={divisions} onNodeClick={onNodeClick} selectedDivisionId={selectedDivisionId} expandedOrder={expandedOrder} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Division;
